import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import { ME_QUERY_KEY } from './auth';

export interface OrganizationOut {
  id: number;
  name: string;
  slug: string;
  part_number_prefix: string;
  prefer_detailed_description: boolean;
  created_at: string;
}

export interface OrganizationMemberOut {
  user_id: number;
  email: string;
  name: string | null;
  role: string;
  joined_at: string;
  is_active: boolean;
}

export interface CreateOrgInput {
  name: string;
  slug?: string;
  part_number_prefix?: string;
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrgInput) =>
      api.postJson<OrganizationOut>('/api/orgs', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useOrgMembers(orgSlug: string | undefined) {
  return useQuery<OrganizationMemberOut[]>({
    queryKey: ['org-members', orgSlug],
    queryFn: () =>
      api.get<OrganizationMemberOut[]>(`/api/orgs/${orgSlug}/members`),
    enabled: Boolean(orgSlug),
  });
}

export function useUpdateMemberRole(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) =>
      api.patchJson<OrganizationMemberOut>(
        `/api/orgs/${orgSlug}/members/${userId}`,
        { role },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-members', orgSlug] }),
  });
}

export function useRemoveMember(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-members', orgSlug] }),
  });
}

export function useLeaveOrg(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<unknown>(`/api/orgs/${orgSlug}/members/me`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['org-members', orgSlug] });
    },
  });
}

export interface OrgUpdateInput {
  name?: string;
  part_number_prefix?: string;
  prefer_detailed_description?: boolean;
}

export function useUpdateOrg(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrgUpdateInput) =>
      api.patchJson<OrganizationOut>(`/api/orgs/${orgSlug}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

export function useDeleteOrg(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<unknown>(`/api/orgs/${orgSlug}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

export interface OrgResetResponse {
  deleted_boms: number;
  deleted_parts: number;
  deleted_locations: number;
}

export function useResetOrg(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmSlug: string) =>
      api.postJson<OrgResetResponse>(`/api/orgs/${orgSlug}/reset`, {
        confirm_slug: confirmSlug,
      }),
    onSuccess: () => {
      // Wipe every org-scoped query so the UI reflects the empty state
      // without a manual refresh — parts list, BOM list, locations,
      // attachments, etc.
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
      qc.invalidateQueries({ queryKey: ['boms', orgSlug] });
      qc.invalidateQueries({ queryKey: ['locations', orgSlug] });
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
